<script setup lang="ts">
  import { computed } from 'vue';
  import { Info, Pencil } from 'lucide-vue-next';
  import { FormInput, Switch, Tooltip } from '@/shared/ui';
  import DirectoryIconPicker from './DirectoryIconPicker.vue';
  import { configureSectionIconClass } from './directoriesPermissions.styles';
  import {
    buildMatrixArchiveTooltip,
    DIRECTORY_MATRIX_ARCHIVE_LABEL,
  } from '../config/directoryMatrixArchive';
  import { DIRECTORY_NODE_COPY } from '../config/directoryNodeCopy';
  import { DIRECTORY_TITLE_MAX_LENGTH } from '../config/directoryTitle';
  import type { DirectoryNodeDetails, DirectoryNodeKind } from '../model/types';

  const ARCHIVE_LABEL_ID = 'directory-matrix-archive-label';

  const props = defineProps<{
    nodeId: string;
    nodeKind: DirectoryNodeKind;
    details: DirectoryNodeDetails;
    iconModelValue: string;
    titleError?: string;
    titleLocked?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:title', value: string): void;
    (e: 'update:iconId', value: string): void;
    (e: 'update:archived', value: boolean): void;
  }>();

  const copy = computed(() => DIRECTORY_NODE_COPY[props.nodeKind]);
  const archived = computed(() => props.details.archived === true);
  const archiveTooltip = computed(() => buildMatrixArchiveTooltip(archived.value));
  const isTitleEditable = computed(() => copy.value.showTitleField && !props.titleLocked);

  const showSavedInDirectoryBy = computed(() => props.nodeKind !== 'module');
</script>

<template>
  <div data-testid="directories-configure-tab" class="flex flex-col gap-4">
    <div
      v-if="!titleLocked"
      data-testid="configure-section-header"
      class="border-b border-gray-02 pb-3"
    >
      <h5 class="text-sm font-semibold text-gray-07">
        {{ copy.sectionTitle }}
      </h5>
      <div
        data-testid="configure-edit-row"
        class="mt-2 flex items-center gap-2 text-sm font-medium text-gray-07"
      >
        <div :class="configureSectionIconClass" data-testid="configure-edit-icon">
          <Pencil class="size-3.5 shrink-0 text-white" :stroke-width="2.25" aria-hidden="true" />
        </div>
        Editar
      </div>
    </div>

    <FormInput
      v-if="isTitleEditable"
      id="directory-node-title"
      :label="copy.titleLabel"
      type="text"
      :placeholder="copy.titlePlaceholder"
      :model-value="details.title"
      :max-length="DIRECTORY_TITLE_MAX_LENGTH"
      :error="titleError"
      @update:model-value="emit('update:title', $event)"
    />

    <div
      v-if="copy.showIconField"
      data-testid="configure-icon-field"
      class="flex flex-col gap-2 border-y border-gray-02 py-4"
    >
      <label class="font-medium text-gray-07">Ícone</label>
      <div class="w-[130px]">
        <DirectoryIconPicker
          :key="nodeId"
          :model-value="iconModelValue"
          :auto-open-when-empty="!iconModelValue"
          @update:model-value="emit('update:iconId', $event)"
        />
      </div>
    </div>

    <div
      v-if="copy.showArchiveField"
      data-testid="configure-archive-field"
      class="flex flex-col border-b border-gray-02 pt-4 pb-8"
    >
      <span :id="ARCHIVE_LABEL_ID" class="flex h-8 items-center text-sm text-gray-07">
        {{ DIRECTORY_MATRIX_ARCHIVE_LABEL }}
      </span>
      <div class="flex items-center">
        <Tooltip :content="archiveTooltip" placement="top">
          <Switch
            size="lg"
            data-testid="configure-archive-switch"
            :model-value="archived"
            :aria-labelledby="ARCHIVE_LABEL_ID"
            @update:model-value="emit('update:archived', $event)"
          />
        </Tooltip>
      </div>
    </div>

    <div class="flex flex-col rounded-lg border border-gray-02">
      <div
        data-testid="configure-info-header"
        class="flex h-[42px] items-center gap-2 overflow-hidden border-b border-gray-02 px-4"
      >
        <div :class="configureSectionIconClass" data-testid="configure-info-icon">
          <Info class="size-3.5 shrink-0 text-white" :stroke-width="2.25" aria-hidden="true" />
        </div>
        <p class="text-sm font-semibold text-gray-07">Informações</p>
      </div>
      <dl class="flex flex-col gap-2 p-4 text-sm text-gray-07">
        <div>
          <dt class="font-semibold">Criado em</dt>
          <dd>{{ details.createdAtLabel }}</dd>
        </div>
        <div>
          <dt class="font-semibold">Criado por</dt>
          <dd>{{ details.createdBy }}</dd>
        </div>
        <div v-if="showSavedInDirectoryBy">
          <dt class="font-semibold">Salvo no Diretório por</dt>
          <dd>{{ details.savedInDirectoryBy }}</dd>
        </div>
      </dl>
    </div>
  </div>
</template>
